import type { CabinState, SemanticIntentResponse, ServiceId } from "../types/domain";

export interface VoiceDestinationSearchRequest {
  id: number;
  serviceId: ServiceId | null;
}

export interface VoiceCommandIntent {
  transcript: string;
  focusService: ServiceId | null;
  matched: boolean;
  message: string;
  destinationSearch?: boolean;
  semantic: SemanticIntentResponse;
  speechText: string;
  statePatch: Partial<CabinState>;
}

export interface VoiceCommandContext {
  activeServiceId?: ServiceId | null;
  state?: CabinState;
  topServiceId?: ServiceId | null;
}

interface VoiceCommandRule {
  focusService: ServiceId | null;
  keywords: string[];
  destinationSearch?: boolean;
  message: string;
  statePatch: Partial<CabinState>;
}

const destinationSearchKeywords = ["导航", "路线", "我要去", "我想去", "我准备去", "导航到", "导航去", "前往", "去目的地", "到目的地", "前往目的地", "去当前目的地", "到当前目的地", "我的目的地", "目的地附近", "附近服务点", "推荐服务点", "搜索服务点", "检索服务点"];
const chargingKeywords = ["充电", "补能", "快充", "电量", "低电", "找桩", "充电桩", "目的地补能"];
const carwashKeywords = ["洗车", "清洗", "车脏", "脏了", "冲车", "汽车美容"];
const refuelKeywords = ["加油", "油量", "油不够", "燃油", "授权码", "加油站"];

const voiceCommandRules: VoiceCommandRule[] = [
  {
    focusService: "parcel",
    keywords: ["快递", "取件", "包裹", "驿站", "菜鸟"],
    message: "已识别取件意图，检查家附近绑定驿站。",
    statePatch: {
      desktop: "ai",
      dayType: "weekday",
      timeOfDay: "commuteHome",
      location: "office",
      parcelPending: true,
      parcelOnRoute: true,
      parked: false,
      traffic: "medium",
      destinationDistanceKm: 18
    }
  },
  {
    focusService: "carwash",
    keywords: carwashKeywords,
    message: "已识别洗车意图，进入顺路洗车判断。",
    statePatch: {
      desktop: "ai",
      dayType: "weekend",
      timeOfDay: "morning",
      weather: "sunny",
      forecastGoodDays: 4,
      vehicleDirty: 86,
      carwashCoupon: true,
      traffic: "light",
      destinationDistanceKm: 18
    }
  },
  {
    focusService: "charging",
    keywords: chargingKeywords,
    message: "已识别补能意图，进入目的地充电判断。",
    statePatch: {
      desktop: "ai",
      batteryLevel: 18,
      chargerAvailability: 6,
      chargerPrice: 1.2,
      destinationDistanceKm: 12,
      traffic: "medium"
    }
  },
  {
    focusService: "parking",
    keywords: ["停车", "缴费", "停车费", "二维码", "扫码", "付停车"],
    message: "已识别停车缴费意图，进入停车助手判断。",
    statePatch: {
      desktop: "ai",
      location: "destination",
      parked: true,
      parkingQrDetected: true,
      destinationDistanceKm: 0,
      parkingAvailability: 28,
      parkingDistanceM: 320,
      traffic: "heavy"
    }
  },
  {
    focusService: "refuel",
    keywords: refuelKeywords,
    message: "已识别加油意图，进入顺路加油判断。",
    statePatch: {
      desktop: "ai",
      dayType: "weekday",
      timeOfDay: "commuteHome",
      batteryLevel: 82,
      fuelLevel: 18,
      destinationDistanceKm: 36,
      traffic: "medium"
    }
  },
  {
    focusService: null,
    keywords: ["高德", "地图", "导航", "路线"],
    message: "已识别地图导航意图，切换到常规地图桌面。",
    statePatch: {
      desktop: "standard"
    }
  },
  {
    focusService: null,
    keywords: ["ai桌面", "智能桌面", "回到ai", "回到智能"],
    message: "已切换到 AI 桌面。",
    statePatch: {
      desktop: "ai"
    }
  }
];

export function interpretVoiceCommand(
  transcript: string,
  context: VoiceCommandContext = {}
): VoiceCommandIntent {
  const normalizedTranscript = normalizeTranscript(transcript);
  const destinationName = extractDestinationName(transcript);
  const destinationSearchRule = getDestinationSearchRule(normalizedTranscript, context, destinationName);

  if (destinationSearchRule) {
    return withVoiceOutput({
      transcript,
      focusService: destinationSearchRule.focusService,
      matched: true,
      message: destinationSearchRule.message,
      destinationSearch: destinationSearchRule.destinationSearch,
      semantic: createLocalSemantic({
        action: "destination_search",
        serviceId: destinationSearchRule.focusService,
        destinationName,
        reply: destinationSearchRule.message
      }),
      statePatch: destinationSearchRule.statePatch
    });
  }

  const rule = voiceCommandRules.find((candidate) =>
    candidate.keywords.some((keyword) => normalizedTranscript.includes(normalizeTranscript(keyword)))
  );

  if (!rule) {
    return withVoiceOutput({
      transcript,
      focusService: null,
      matched: false,
      message: "已识别文字，暂未命中可执行意图。",
      semantic: createLocalSemantic({
        action: "unknown",
        serviceId: null,
        destinationName: null,
        confidence: 0.25,
        reply: "我还没理解这条指令，可以换一种说法。"
      }),
      statePatch: {}
    });
  }

  return withVoiceOutput({
    transcript,
    focusService: rule.focusService,
    matched: true,
    message: rule.message,
    semantic: createLocalSemantic({
      action: rule.focusService ? "service_request" : "switch_desktop",
      serviceId: rule.focusService,
      destinationName: null,
      reply: rule.message
    }),
    statePatch: rule.statePatch
  });
}

export function applySemanticInterpretation(
  transcript: string,
  context: VoiceCommandContext,
  semantic: SemanticIntentResponse | null
) {
  const localIntent = interpretVoiceCommand(transcript, context);

  if (!semantic || semantic.source !== "model" || semantic.confidence < 0.35) {
    return localIntent;
  }

  if (semantic.action === "destination_search") {
    const normalizedTranscript = normalizeTranscript(transcript);
    const serviceId = isDestinationServiceId(semantic.serviceId)
      ? semantic.serviceId
      : getContextServiceId(context) ?? getExplicitServiceId(normalizedTranscript) ?? "charging";
    const destinationName = semantic.destinationName ?? extractDestinationName(transcript);
    const rule = buildDestinationSearchRule(serviceId, context, destinationName, semantic.reply);

    return withVoiceOutput({
      transcript,
      focusService: rule.focusService,
      matched: true,
      message: rule.message,
      destinationSearch: true,
      semantic,
      statePatch: rule.statePatch
    });
  }

  if (semantic.action === "service_request" && semantic.serviceId) {
    const rule = getRuleByServiceId(semantic.serviceId);

    if (rule) {
      return withVoiceOutput({
        transcript,
        focusService: rule.focusService,
        matched: true,
        message: semantic.reply || rule.message,
        semantic,
        statePatch: rule.statePatch
      });
    }
  }

  if (semantic.action === "switch_desktop") {
    const wantsAiDesktop = /ai|智能/.test(normalizeTranscript(transcript));
    return withVoiceOutput({
      transcript,
      focusService: null,
      matched: true,
      message: semantic.reply || (wantsAiDesktop ? "已切换到 AI 桌面。" : "已切换到常规地图桌面。"),
      semantic,
      statePatch: {
        desktop: wantsAiDesktop ? "ai" : "standard"
      }
    });
  }

  if (semantic.action === "unknown" && !localIntent.matched) {
    return withVoiceOutput({
      ...localIntent,
      message: semantic.reply || localIntent.message,
      semantic
    });
  }

  return localIntent;
}

function getDestinationSearchRule(
  normalizedTranscript: string,
  context: VoiceCommandContext,
  destinationName: string | null
): VoiceCommandRule | null {
  if (!hasKeyword(normalizedTranscript, destinationSearchKeywords)) {
    return null;
  }

  const serviceId = getContextServiceId(context) ?? getExplicitServiceId(normalizedTranscript);
  const destinationLabel = (destinationName ?? context.state?.destinationName.trim()) || "目的地";

  if (isDestinationServiceId(serviceId)) {
    return buildDestinationSearchRule(serviceId, context, destinationName);
  }

  return buildDestinationSearchRule("charging", context, destinationName, `已模拟目的地检索：生成到${destinationLabel}的地图路线，并根据当前车况推荐附近服务点。`);
}

function getExplicitServiceId(normalizedTranscript: string): "charging" | "carwash" | "refuel" | null {
  if (hasKeyword(normalizedTranscript, chargingKeywords)) {
    return "charging";
  }

  if (hasKeyword(normalizedTranscript, carwashKeywords)) {
    return "carwash";
  }

  if (hasKeyword(normalizedTranscript, refuelKeywords)) {
    return "refuel";
  }

  return null;
}

function getContextServiceId(context: VoiceCommandContext): "charging" | "carwash" | "refuel" | null {
  if (isDestinationServiceId(context.activeServiceId)) {
    return context.activeServiceId;
  }

  if (isDestinationServiceId(context.topServiceId)) {
    return context.topServiceId;
  }

  const { state } = context;

  if (!state) {
    return null;
  }

  if (state.batteryLevel <= 28 || state.rangeKm <= 160) {
    return "charging";
  }

  if (
    state.dayType === "weekend" &&
    state.weather !== "rainy" &&
    (state.vehicleDirty >= 66 || state.carwashCoupon)
  ) {
    return "carwash";
  }

  if (state.fuelLevel <= 30 || state.fuelRangeKm <= 220) {
    return "refuel";
  }

  return null;
}

function getDestinationSearchPatch(
  serviceId: "charging" | "carwash" | "refuel",
  state?: CabinState,
  destinationName?: string | null
): Partial<CabinState> {
  const basePatch: Partial<CabinState> = {
    desktop: "standard",
    ...(destinationName ? { destinationName } : {})
  };

  if (!state) {
    return basePatch;
  }

  if (serviceId === "charging") {
    return {
      ...basePatch,
      batteryLevel: Math.min(state.batteryLevel, 18),
      chargerAvailability: Math.max(state.chargerAvailability, 6),
      chargerPrice: Math.min(state.chargerPrice, 1.2),
      destinationDistanceKm: Math.max(state.destinationDistanceKm, 12),
      traffic: state.traffic === "heavy" ? "medium" : state.traffic
    };
  }

  if (serviceId === "carwash") {
    return {
      ...basePatch,
      dayType: "weekend",
      forecastGoodDays: Math.max(state.forecastGoodDays, 4),
      vehicleDirty: Math.max(state.vehicleDirty, 86),
      carwashCoupon: true,
      weather: state.weather === "rainy" ? "sunny" : state.weather,
      destinationDistanceKm: Math.max(state.destinationDistanceKm, 18),
      traffic: state.traffic === "heavy" ? "medium" : state.traffic
    };
  }

  return {
    ...basePatch,
    fuelLevel: Math.min(state.fuelLevel, 18),
    fuelRangeKm: Math.min(state.fuelRangeKm, 180),
    destinationDistanceKm: Math.max(state.destinationDistanceKm, 36),
    traffic: state.traffic === "heavy" ? "medium" : state.traffic
  };
}

function buildDestinationSearchRule(
  serviceId: "charging" | "carwash" | "refuel",
  context: VoiceCommandContext,
  destinationName: string | null,
  messageOverride?: string
): VoiceCommandRule {
  const destinationLabel = (destinationName ?? context.state?.destinationName.trim()) || "目的地";
  const serviceLabel = serviceId === "charging" ? "快充站" : serviceId === "carwash" ? "洗车点" : "加油站";

  return {
    focusService: serviceId,
    keywords: [],
    destinationSearch: true,
    message: messageOverride || `已模拟目的地检索：生成到${destinationLabel}的地图路线，并根据当前车况推荐附近${serviceLabel}。`,
    statePatch: getDestinationSearchPatch(serviceId, context.state, destinationName)
  };
}

function getRuleByServiceId(serviceId: ServiceId) {
  return voiceCommandRules.find((rule) => rule.focusService === serviceId) ?? null;
}

function createLocalSemantic({
  action,
  serviceId,
  destinationName,
  reply,
  confidence = 0.86
}: {
  action: SemanticIntentResponse["action"];
  serviceId: ServiceId | null;
  destinationName: string | null;
  reply: string;
  confidence?: number;
}): SemanticIntentResponse {
  return {
    source: "local",
    action,
    serviceId,
    destinationName,
    confidence,
    reply
  };
}

function withVoiceOutput(intent: Omit<VoiceCommandIntent, "speechText">): VoiceCommandIntent {
  return {
    ...intent,
    speechText: intent.semantic.reply || intent.message
  };
}

function isDestinationServiceId(serviceId: ServiceId | null | undefined): serviceId is "charging" | "carwash" | "refuel" {
  return serviceId === "charging" || serviceId === "carwash" || serviceId === "refuel";
}

function hasKeyword(normalizedTranscript: string, keywords: string[]) {
  return keywords.some((keyword) => normalizedTranscript.includes(normalizeTranscript(keyword)));
}

function extractDestinationName(transcript: string) {
  const compactTranscript = transcript.trim().replace(/\s+/g, "");

  if (!compactTranscript) {
    return null;
  }

  const patterns = [
    /(?:我要去|我想去|我准备去|帮我去|带我去|导航去|导航到|前往|去一下|去到|到)([^，。！？、,.!?]+)/u,
    /去([^，。！？、,.!?]+)/u
  ];

  for (const pattern of patterns) {
    const match = compactTranscript.match(pattern);
    const destinationName = normalizeDestinationCandidate(match?.[1] ?? "");

    if (destinationName) {
      return destinationName;
    }
  }

  return null;
}

function normalizeDestinationCandidate(candidate: string) {
  let value = candidate
    .replace(/^(一下|一趟|的是|是)/u, "")
    .trim();
  const stopWords = [
    "帮我",
    "给我",
    "为我",
    "然后",
    "顺便",
    "附近",
    "周边",
    "旁边",
    "周末",
    "顺路",
    "导航",
    "路线",
    "检索",
    "搜索",
    "推荐",
    "查找",
    "找",
    "服务点",
    "充电站",
    "充电桩",
    "快充站",
    "洗车点",
    "洗车店",
    "加油站",
    "加油点",
    "充电",
    "补能",
    "洗车",
    "加油"
  ];
  const cutIndex = stopWords.reduce((currentIndex, stopWord) => {
    const index = value.indexOf(stopWord);
    if (index < 0) {
      return currentIndex;
    }

    return currentIndex < 0 ? index : Math.min(currentIndex, index);
  }, -1);

  if (cutIndex >= 0) {
    value = value.slice(0, cutIndex);
  }

  value = value.replace(/^(去|到|前往)/u, "").replace(/[吧吗呢啦了]$/u, "");

  if (!value || value.includes("目的地")) {
    return null;
  }

  const genericNames = ["服务点", "附近服务点", "周末洗车", "顺路加油", "附近"];
  return genericNames.includes(value) ? null : value;
}

function normalizeTranscript(text: string) {
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[，。！？、,.!?]/g, "");
}
